<template>
  <NTable
    v-if="isSuccessVirtualPortfolioSymbols && portfolioSymbols.length"
    :data="portfolioSymbols"
    :sort-by="{
      prop: 'value',
      order: 'descending',
    }"
    cell-padding="small"
    :column-splitter="false"
    :striped="false"
    class="mb-2 h-full"
    hover-alignment="right"
    hover-offset="15%"
    virtual-scroll
    fixed-header
  >
    <template #hoveredElements="{ row }">
      <span
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'virtualPortfolioTable',
          component: 'editButton',
        }"
        class="ml-1 p-1 active:bg-neutral-secondary hover:bg-neutral-secondary rounded-full cursor-pointer"
        @click.stop="
          openEditVirtualPortfolioNetAveragePriceDialog({
            selectedSymbol: row,
            referenceElementRef: netAveragePriceRef,
          })
        "
      >
        <NegarEditBorderlessIcon ref="netAveragePriceRef" size="20" />
      </span>

      <span
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'virtualPortfolioTable',
          component: 'deleteButton',
        }"
        class="ml-1 p-1 active:bg-neutral-secondary hover:bg-neutral-secondary rounded-full cursor-pointer"
        @click.stop="
          openDeleteVirtualPortfolioItemDialog({
            selectedSymbol: row,
            referenceElementRef: deleteIconRef,
            onDelete: () => deleteVirtualPortfolioItem(row),
            loading: isLoadingDeleteVirtualPortfolioItem,
          })
        "
      >
        <NegarTrashLightIcon ref="deleteIconRef" size="20" color="red" />
      </span>
    </template>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.symbol')"
      prop="instrumentName"
      sortable
      class="flex items-center"
      align="text-right"
    >
      <SymbolTradeStateIcon
        class="me-1"
        :trading-state-code="row.instrumentTradingState"
      />
      <NText :key="row.instrumentName" variant="caption2" weight="medium">
        {{ row.instrumentName }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.quantity')"
      prop="quantity"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.quantity">
        <ChangePriceEffect :key="row.quantity" :price="row.quantity">
          <NText variant="caption2" weight="medium">
            {{ number }}
          </NText>
        </ChangePriceEffect>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.net_average')"
      prop="netAveragePrice"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.netAveragePrice" round>
        <NText
          :key="row.netAveragePrice"
          dir="ltr"
          variant="caption2"
          weight="medium"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.total_buy')"
      prop="symbolBuyValue"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.symbolBuyValue" round>
        <NText
          :key="row.symbolBuyValue"
          dir="ltr"
          variant="caption2"
          weight="medium"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.break_point')"
      prop="breakPoint"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.breakPoint" round>
        <NText
          :key="row.breakPoint"
          dir="ltr"
          variant="caption2"
          weight="medium"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.current_price')"
      prop="lastTradePrice"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.lastTradePrice">
        <ChangePriceEffect :price="row.lastTradePrice">
          <NText
            :key="row.lastTradePrice"
            dir="ltr"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </ChangePriceEffect>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('misc.value')"
      prop="value"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.value" round>
        <NText :key="row.value" dir="ltr" variant="caption2" weight="medium">
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="`${t('modules.portfolio.profit')}/${t('modules.portfolio.loss')}`"
      prop="returnValue"
      sortable
      align="text-right"
    >
      <div :key="row.returnValuePercentage" class="flex items-center">
        <NumberFormatter
          #="{ number }"
          :value="row.returnValuePercentage / 100"
          number-style="percent"
          maximum-fraction-digits="2"
          sign-display="always"
        >
          <NText
            class="align-middle"
            dir="ltr"
            :class="{
              'text-brand-primary': row.returnValuePercentage > 0,
              'text-critical-primary': row.returnValuePercentage < 0,
            }"
            variant="caption2"
            weight="medium"
          >
            ({{ number }})
          </NText>
        </NumberFormatter>

        <NumberFormatter
          #="{ number }"
          :value="row.returnValue"
          sign-display="always"
          round
        >
          <NText
            class="align-middle mx-1 transition-all duration-300"
            :class="{
              'text-brand-primary': row.returnValuePercentage > 0,
              'text-critical-primary': row.returnValuePercentage < 0,
            }"
            variant="caption2"
            weight="medium"
            dir="ltr"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>
    </NTableColumn>
  </NTable>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarEditBorderlessIcon, NegarTrashLightIcon } from '@negar/icons';
import {
  PORTFOLIO_QUERY_KEYS,
  useDeleteVirtualPortfolioItemQuery,
  useLiveVirtualPortfolioQuery,
} from '@core/portfolio';
import { useQueryClient } from '@libs/use';
import { useDialog, useToast } from '@negar/core';

import { usePortfolioDialogs } from '@/modules/portfolio';
import { SymbolTradeStateIcon } from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

const { t } = useI18n();

const netAveragePriceRef = ref<HTMLElement>();
const deleteIconRef = ref<HTMLElement>();

const {
  openDeleteVirtualPortfolioItemDialog,
  openEditVirtualPortfolioNetAveragePriceDialog,
} = usePortfolioDialogs();

const toast = useToast();
const { remove } = useDialog();
const queryClient = useQueryClient();

const { symbols: portfolioSymbols, isSuccessVirtualPortfolioSymbols } =
  useLiveVirtualPortfolioQuery();

const { deleteVirtualPortfolioItem, isLoadingDeleteVirtualPortfolioItem } =
  useDeleteVirtualPortfolioItemQuery({
    onSuccess(_, contextData) {
      toast.neutral({
        message: t('modules.portfolio.virtualPortfolio.deleteSuccessMessage', {
          name: contextData.instrumentName,
        }),
        duration: 3000,
      });

      queryClient.invalidateQueries(PORTFOLIO_QUERY_KEYS.virtualPortfolio);

      remove();
    },
    onReject(_, contextData) {
      toast.negative({
        message: t('modules.portfolio.virtualPortfolio.deleteFailedMessage', {
          name: contextData.instrumentName,
        }),
        duration: 3000,
      });
    },
  });
</script>
